import { ChangeEvent, useState, useEffect } from "react";
import { useLicenses, useAccountAdmins } from "hooks/useLicense";
import Cookies from "js-cookie";

import { HierarchyProps } from "types/module/License";

import Skeleton from "components/ui/Preloader/Skeleton";
import TreeView from "components/ui/TreeView/TreeView";

import { Input, ActionIcon, Box, Text } from "@mantine/core";
import { FaSearch } from "react-icons/fa";
import { getManagerEmail } from "helpers";

// Hierarchy is only rendered when assignmentFilter === 'all' or user is not an LM
// When LM uses "Assigned to me", the hierarchy panel is hidden entirely by License.tsx
const Hierarchy: React.FC<HierarchyProps> = ({ handleSelectAccount, assignmentFilter = 'all' }) => {
	const { data: licenseData, isLoading: isLicenseDataLoading } = useLicenses();
	const { data: accountAdminsData } = useAccountAdmins();
	const [accountFilter, setAccountFilter] = useState<string>("");
	const [filteredLicenseData, setFilteredLicenseData] = useState<any>(null);
	const userEmail = Cookies.get("email");

	const currentUser = accountAdminsData?.find((m: any) => {
		const email = getManagerEmail(m) || m.Email;
		return email?.toLowerCase() === userEmail?.toLowerCase();
	});
	const userId = currentUser?.Id || currentUser?.id;

	// Check if user is a License Manager (not admin/super user)
	const accountType = Cookies.get("accountType");
	const isLicenseManager = accountType === "oem_customer_license_manager";

	const handleAccountSearch = (e: ChangeEvent<HTMLInputElement>): void => {
		setAccountFilter(e.target.value);
	};

	// Apply filtering based on assignmentFilter
	useEffect(() => {
		if (!licenseData) {
			setFilteredLicenseData(null);
			return;
		}

		// If filter is 'all' or user email is not available, show all data
		if (assignmentFilter === 'all' || !userEmail) {
			setFilteredLicenseData(null); // null means use original licenseData
			return;
		}

		// Filter for 'assigned' - for admins/super users only
		// (LMs already handled above with myHierarchyData)
		if (assignmentFilter === 'assigned' && !isLicenseManager) {
			// For Admins/Super Users: check if user's email is in licenseManagers array
			// Helper function to check if a pool is assigned to the user
			const isPoolAssigned = (license: any): boolean => {
				if (!license.licenseManagers || !Array.isArray(license.licenseManagers)) return false;
				return license.licenseManagers.some((m: any) =>
					getManagerEmail(m)?.toLowerCase() === userEmail?.toLowerCase() ||
					m.Id === userId
				);
			};

			// Helper function to check if an account or its children have any assigned pools
			const hasAssignedPools = (account: any): boolean => {
				if (account.licenses && Array.isArray(account.licenses)) {
					if (account.licenses.some(isPoolAssigned)) return true;
				}
				if (account.accounts && Array.isArray(account.accounts)) {
					return account.accounts.some(hasAssignedPools);
				}
				return false;
			};

			// Filter and transform the hierarchy recursively
			const filterAccount = (account: any): any => {
				const filteredLicenses = account.licenses
					? account.licenses.filter(isPoolAssigned)
					: [];

				const filteredChildren = account.accounts
					? account.accounts
						.filter(hasAssignedPools)
						.map(filterAccount)
					: [];

				return {
					...account,
					licenses: filteredLicenses,
					accounts: filteredChildren
				};
			};

			// Filter the hierarchy
			if (hasAssignedPools(licenseData)) {
				const filtered = filterAccount(licenseData);
				setFilteredLicenseData(filtered);
			} else {
				// No assigned pools - show empty structure
				setFilteredLicenseData({
					...licenseData,
					licenses: [],
					accounts: []
				});
			}
		}
	}, [licenseData, assignmentFilter, userId, userEmail, isLicenseManager]);

	const displayData = assignmentFilter === 'assigned' ? filteredLicenseData : licenseData;
	const isLoading = isLicenseDataLoading;

	return (
		<>
			<Box mb="md">
				<Text size="lg" fw={600} mb="sm">Accounts</Text>
				{!(displayData?.length == 0 || isLoading) && (
					<Input
						placeholder="Search accounts..."
						value={accountFilter}
						onChange={handleAccountSearch}
						size="xs"
						leftSection={<FaSearch size={11} style={{ color: '#868e96' }} />}
						rightSection={
							accountFilter && (
								<ActionIcon
									variant="transparent"
									size="xs"
									onClick={() => setAccountFilter('')}
									style={{ color: '#868e96' }}
								>
									×
								</ActionIcon>
							)
						}
						styles={{
							input: {
								borderRadius: '6px',
								fontSize: '12px',
							}
						}}
					/>
				)}
			</Box>
			{isLoading ? (
				<Skeleton />
			) : !isLoading && displayData ? (
				<TreeView
					key={assignmentFilter}
					data={displayData}
					dataFilter={accountFilter}
					config={{
						id: "accountId",
						parent: "name",
						children: "accounts",
						toReturn: ["isTopLevelAccount", "licenses", "accountId"],
					}}
					onSelectNode={handleSelectAccount}
					expandFirst={true}
				/>
			) : (
				""
			)}
		</>
	);
};

export default Hierarchy;
